import { Controller } from "@hotwired/stimulus";

// Tells the CSM cockpit where the customer is standing and whether they are
// typing.
//
// Before this, an operator's only clue about the customer's screen was
// Message#page_url — which arrives WITH the message, i.e. one message after it
// would have been useful. The most common thing a human had to type into the
// cockpit was "which screen are you on?", to somebody who was standing on it.
//
// Three signals, one endpoint (KitController#presence → KitPresence):
//
//   * On connect, and whenever the tab becomes visible: "they are here, on this
//     page". The panel is a turbo-frame inside the customer's real page, so
//     window.location IS the screen they are looking at.
//   * A heartbeat while the tab is visible, so "here now" can expire on its own
//     rather than lying about someone who closed the laptop.
//   * typing=true, throttled, while they are actually putting words in the box.
//
// Everything is best-effort and silent. A presence beacon is never worth a
// console error, a retry, or a millisecond of the customer's attention.
export default class extends Controller {
  static values = { url: String, chatId: Number };

  connect() {
    // Comfortably under KitPresence::HERE_WINDOW (75s), so a live panel never
    // flickers out of "here now" between beats.
    this.HEARTBEAT_MS = 30000;
    // Long enough that a fast typist sends a handful of pings per sentence,
    // short enough to stay inside KitPresence::TYPING_TTL (8s) so the indicator
    // in the cockpit doesn't blink while they are still typing.
    this.TYPING_MS = 3000;

    this.lastTyping = 0;

    // Delegated rather than bound to the textarea: sending a message REPLACES
    // the whole form (messages/create.turbo_stream.erb), so a listener on the
    // box itself would be thrown away after the first message — the same trap
    // chat_input_controller documents about the hidden page fields. This
    // element wraps the form and survives.
    this.onInput = (event) => {
      if (!event.target || event.target.tagName !== "TEXTAREA") return;
      const now = Date.now();
      if (now - this.lastTyping < this.TYPING_MS) return;
      this.lastTyping = now;
      this.report({ typing: true });
    };
    this.element.addEventListener("input", this.onInput);

    this.onVisibility = () => {
      if (!document.hidden) this.report();
    };
    document.addEventListener("visibilitychange", this.onVisibility);

    // A backgrounded tab is not presence — nobody is looking at that page, and
    // an operator reading "here now" about it would be reading a lie.
    this.timer = setInterval(() => {
      if (!document.hidden) this.report();
    }, this.HEARTBEAT_MS);

    this.report();
  }

  disconnect() {
    this.element.removeEventListener("input", this.onInput);
    document.removeEventListener("visibilitychange", this.onVisibility);
    if (this.timer) clearInterval(this.timer);
  }

  report({ typing = false } = {}) {
    if (!this.hasUrlValue || !this.hasChatIdValue) return;

    const token = document.querySelector('meta[name="csrf-token"]');
    fetch(this.urlValue, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": token ? token.getAttribute("content") : "",
        "X-Requested-With": "XMLHttpRequest",
      },
      body: JSON.stringify({
        chat_id: this.chatIdValue,
        // pathname + search, never the origin: the cockpit renders this against
        // the tenant's own subdomain, and a full URL would let the client
        // choose the host it links an operator to.
        page_path: window.location.pathname + window.location.search,
        page_title: document.title,
        typing: typing,
      }),
      keepalive: true,
    }).catch(() => {
      // Offline, or the tab is closing. The next beat re-reports.
    });
  }
}
